// Used in reports-standards, reports-scores-student, reports-scores, reports-sessions

// Angular
import { Injectable } from "@angular/core";

// Services/Types/Helpers
import { PPAssignment as Assignment } from "cli-types";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { AssignmentStatusHelper } from "#helpers/assignment-status.helper";

// Moment
import moment from "moment";

@Injectable()
export class CsvExportHelper {
    constructor(
        private timezoneHelper: TimezoneHelper,
        private assignmentStatusHelper: AssignmentStatusHelper
    ) {}

    csvAssignmentScoreFormatter(data: Assignment) {
        /**
         * The numerical value that represents the score assigned.
         * Empty/Blank Space, for assignments not completed
         * "Overdue", for assignments that are overdue and not submitted
         * Add an asterisk to a score that was submitted late
         */

        if (data.status === "overdue") {
            return "Overdue";
        }

        if (!data.completedAt && !data.evaluatedAt) {
            return "";
        }

        if (data.evaluatorScore !== null && !isNaN(data.evaluatorScore)) {
            const submittedLate =
                data.completedAt &&
                new Date(data.completedAt).getTime() >
                    new Date(data.dueDate).getTime();

            if (submittedLate) {
                return "* " + data.evaluatorScore;
            } else {
                return data.evaluatorScore;
            }
        }
        return "";
    }

    formatCSVDate(timezone: string, date: string | null): string | null {
        if (!date) {
            return "";
        } else {
            const updatedDateTime =
                this.timezoneHelper.convertServerDateToUserTZ(date, timezone);
            return moment(updatedDateTime).format("llll");
        }
    }

    formatCSVAssignmentStatus(status: string): string | null {
        if (!status) {
            return "";
        } else {
            let formattedStatus =
                this.assignmentStatusHelper.getStatusName(status);
            if (status === "inProgress") {
                formattedStatus = "Not Submitted";
            }
            return formattedStatus;
        }
    }

    formatCSVFilename(
        reportName: string,
        schoolName: string,
        teacherName: string,
        className: string,
        studentName: string,
        fancyDate: string,
        studentCount: number | undefined
    ): string {
        className = className?.slice(0, 64); // className needs to be truncated, some are too long
        schoolName = schoolName?.slice(0, 64); // schoolName needs to be truncated, some are too long
        let fileName: string =
                reportName +
                " - " +
                schoolName +
                " - " +
                teacherName +
                " - " +
                className,
            date: string;
        if (studentName !== "class") {
            fileName += " - " + studentName; // student level, append their name
        } else {
            if (studentCount === 1) {
                fileName += " - " + studentCount + " student";
            } else {
                fileName += " - " + studentCount + " students";
            }
        }
        date =
            fancyDate !== "normal"
                ? fancyDate
                : new Date().toISOString().slice(0, 10);
        fileName += " - " + date + ".csv";
        return fileName;
    }
}
